import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Copy, Download, BookOpen, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

/* HTML spec the backend parser accepts. Shown inside the "Import HTML" dialog. */

const SAMPLE_HTML = `<!doctype html>
<html><body>

<!-- ============ QUESTION 1 (Single correct, bilingual) ============ -->
<div class="question">
  <p class="prompt en">
    What is the FIRST of WHO's 5 Moments of Hand Hygiene?
    <img src="https://images.example.com/who-5-moments.png" alt="WHO 5 moments">
  </p>
  <p class="prompt hi">
    WHO के हाथ की स्वच्छता के 5 क्षणों में से पहला कौन सा है?
  </p>

  <!-- English options -->
  <ul class="en">
    <li>After touching a patient</li>
    <li class="correct">Before touching a patient</li>
    <li>Before a clean / aseptic task</li>
    <li>After body fluid exposure</li>
  </ul>

  <!-- Hindi options (same order, same correct row) -->
  <ul class="hi">
    <li>रोगी को छूने के बाद</li>
    <li class="correct">रोगी को छूने से पहले</li>
    <li>सफाई / एसेप्टिक कार्य से पहले</li>
    <li>शारीरिक तरल के संपर्क के बाद</li>
  </ul>

  <p class="explanation en">Hand hygiene before patient contact prevents transmission of healthcare-associated infections.</p>
  <p class="explanation hi">रोगी के संपर्क से पहले हाथ की स्वच्छता संक्रमण के प्रसार को रोकती है।</p>
</div>

<!-- ============ QUESTION 2 (Multi-correct, inline bilingual spans) ============ -->
<div class="question">
  <p class="prompt en">Select ALL PPE required for COVID-19 patient care:</p>
  <p class="prompt hi">COVID-19 रोगी देखभाल के लिए आवश्यक सभी PPE चुनें:</p>

  <ul>
    <li class="correct">
      <span class="en">N95 respirator</span>
      <span class="hi">N95 रेस्पिरेटर</span>
    </li>
    <li class="correct">
      <span class="en">Face shield</span>
      <span class="hi">फेस शील्ड</span>
    </li>
    <li>
      <span class="en">Cotton gloves</span>
      <span class="hi">सूती दस्ताने</span>
    </li>
    <li class="correct">
      <span class="en">Gown</span>
      <span class="hi">गाउन</span>
    </li>
  </ul>
</div>

</body></html>`;

const CLAUDE_PROMPT = `You are an expert medical-training quiz author. Generate a quiz of 10 multiple-choice questions on the topic I will provide. The output MUST be a single self-contained HTML document that follows this exact structure (copy-paste it into the hospital training platform's "Import HTML" feature):

RULES
1. Wrap every question in <div class="question"> ... </div>.
2. For each question, include BOTH languages:
     <p class="prompt en">English prompt</p>
     <p class="prompt hi">हिंदी प्रश्न</p>
3. Provide EXACTLY 4 options. Two acceptable formats — pick ONE per question:
     A) Two parallel lists (recommended for clarity):
        <ul class="en"><li>…</li><li class="correct">…</li>…</ul>
        <ul class="hi"><li>…</li><li class="correct">…</li>…</ul>
        — Same option order in both lists. Mark the SAME index correct in both.
     B) Single list with inline spans:
        <ul>
          <li class="correct"><span class="en">EN text</span><span class="hi">हिंदी पाठ</span></li>
          ...
        </ul>
4. Mark correct answer(s) by adding class="correct" to the <li>. For multi-select questions mark multiple <li> with class="correct" — the platform auto-detects single vs multi.
5. (Optional) Add explanation paragraphs:
     <p class="explanation en">Why this answer is correct…</p>
     <p class="explanation hi">यह उत्तर सही क्यों है…</p>
6. Images: use <img src="ABSOLUTE_URL" alt="..."> inside the prompt. URLs must be publicly reachable (Wikimedia, Unsplash, hospital CDN, etc.). DO NOT embed base64.
7. NEVER use <script>, <style>, or inline event handlers (onClick etc.) — they will be stripped.
8. Keep prompts under ~300 characters each. Options under ~120 characters.
9. Output ONLY the HTML document — no explanations, no markdown code fences.

TOPIC: <FILL THIS IN — e.g. "Infection Control for Nursing Staff", "Fire Safety Drill", "BLS Basics">
NUMBER OF QUESTIONS: 10
MIX: 7 single-correct, 3 multi-select
LEVEL: Practical, scenario-based (avoid trivia)
`;

export default function HtmlImportHelp() {
  const [open, setOpen] = useState(false);

  function copy(text, label) {
    navigator.clipboard.writeText(text).then(
      () => toast.success(`${label} copied to clipboard`),
      () => toast.error("Copy failed — please select and copy manually"),
    );
  }
  function downloadFile(text, name) {
    const blob = new Blob([text], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = name; a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="rounded-xl border border-violet-200 bg-violet-50/70 overflow-hidden" data-testid="html-import-help">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-3.5 py-2.5 text-sm font-bold text-violet-900 hover:bg-violet-100/50 transition"
        data-testid="html-help-toggle"
      >
        <span className="flex items-center gap-2"><BookOpen className="h-4 w-4" />Show format reference & Claude AI prompt</span>
        {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>
      {open && (
        <div className="p-3.5 space-y-4 border-t border-violet-200 bg-white">
          {/* Quick rules */}
          <div className="text-xs text-slate-600 space-y-1">
            <div className="font-bold text-slate-900 text-sm mb-1">Required structure</div>
            <ul className="list-disc pl-5 space-y-1">
              <li>Each question wrapped in <code className="px-1 bg-slate-100 rounded">&lt;div class="question"&gt;</code></li>
              <li>Bilingual prompts: <code className="px-1 bg-slate-100 rounded">&lt;p class="prompt en"&gt;…&lt;/p&gt;</code> + <code className="px-1 bg-slate-100 rounded">&lt;p class="prompt hi"&gt;…&lt;/p&gt;</code></li>
              <li>Exactly <b>4 options</b> per question — paired lists (<code className="px-1 bg-slate-100 rounded">&lt;ul class="en"&gt;</code> + <code className="px-1 bg-slate-100 rounded">&lt;ul class="hi"&gt;</code>) OR single list with inline <code className="px-1 bg-slate-100 rounded">&lt;span class="en|hi"&gt;</code></li>
              <li>Mark right answer(s) with <code className="px-1 bg-slate-100 rounded">class="correct"</code> on the <code className="px-1 bg-slate-100 rounded">&lt;li&gt;</code>. Multiple = multi-select auto-detected.</li>
              <li>Images via <code className="px-1 bg-slate-100 rounded">&lt;img src="ABSOLUTE_URL"&gt;</code> inside the prompt (no base64).</li>
              <li>Scripts, styles, inline JS are stripped for security.</li>
            </ul>
          </div>

          {/* Claude prompt */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <div className="font-bold text-slate-900 text-sm">Claude AI prompt template</div>
              <div className="flex gap-1.5">
                <Button size="sm" variant="outline" onClick={() => copy(CLAUDE_PROMPT, "Claude prompt")} className="h-7 text-xs" data-testid="copy-claude-prompt">
                  <Copy className="h-3 w-3 mr-1" />Copy
                </Button>
              </div>
            </div>
            <pre className="text-[11px] bg-slate-900 text-slate-100 rounded-lg p-3 max-h-60 overflow-y-auto whitespace-pre-wrap font-mono">{CLAUDE_PROMPT}</pre>
            <div className="text-[11px] text-slate-500 mt-1">Paste this into Claude (claude.ai or any Claude model). Replace the <b>TOPIC</b> line with what you need, then paste Claude's output back here.</div>
          </div>

          {/* Sample HTML */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <div className="font-bold text-slate-900 text-sm">Sample HTML (bilingual + image + single & multi)</div>
              <div className="flex gap-1.5">
                <Button size="sm" variant="outline" onClick={() => copy(SAMPLE_HTML, "Sample HTML")} className="h-7 text-xs" data-testid="copy-sample-html">
                  <Copy className="h-3 w-3 mr-1" />Copy
                </Button>
                <Button size="sm" variant="outline" onClick={() => downloadFile(SAMPLE_HTML, "quiz-sample.html")} className="h-7 text-xs" data-testid="download-sample-html">
                  <Download className="h-3 w-3 mr-1" />.html
                </Button>
              </div>
            </div>
            <pre className="text-[11px] bg-slate-50 border border-slate-200 rounded-lg p-3 max-h-60 overflow-y-auto whitespace-pre font-mono">{SAMPLE_HTML}</pre>
          </div>

          {/* Quick-load into the textarea */}
          <div className="text-[11px] text-slate-500 italic">
            Tip — click the "Copy" button on the sample, then paste it in the textarea below to test the import flow without using Claude.
          </div>
        </div>
      )}
    </div>
  );
}

export { SAMPLE_HTML, CLAUDE_PROMPT };
